import { CustomLink_LinkType } from '@weave/schema-gen-ts/dist/schemas/brand-settings/v1/settings.pb';
import {
  BrandSetting,
  CustomLink_LinkType as LinkType,
} from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/settings.pb';
import { ButtonSection as ButtonSectionType } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { SetupState } from '@weave/schema-gen-ts/dist/schemas/reviews/shared/v1/review_request.pb';
import { i18next } from '@frontend/i18n';
import { CommonErrors } from '../../content-composer/common-errors';
import { EmailComposerLinkType } from '../../content-composer/types';
import { createSectionValidator } from '../../content-composer/utils/create-section-validator';

const t = i18next.getFixedT(null, 'content-composer');
const hasValue = (value?: string) => !!value?.trim();

export const ButtonValidator = createSectionValidator<ButtonSectionType>()({
  errors: {
    MissingText: {
      code: 1,
      details: t('Button text is required.'),
      summary: (count: number) => t('{{count}} missing button labels', { count }),
    },
    MissingLink: {
      code: 2,
      details: t('Button link is required.'),
      summary: (count: number) => t('{{count}} missing button links', { count }),
    },
  },
  validate: async ({ context, errors, props, reportError }) => {
    if (!hasValue(props.link?.url)) {
      reportError(errors.MissingLink);
    }

    if (!hasValue(props.link?.text)) {
      reportError(errors.MissingText);
    }

    const linkType = props.link?.linkType as EmailComposerLinkType | undefined;
    const locationIds = context.locationIds ?? [];
    const brandSettings = context.brandSettings;
    if (!linkType || locationIds.length === 0 || !brandSettings) return;

    const countFailingLocations = (predicate: (setting: BrandSetting) => boolean) => {
      return locationIds.reduce((count, locationId) => {
        const setting = brandSettings[locationId];
        if (!setting || !predicate(setting)) return count + 1;
        return count;
      }, 0);
    };

    const countMissingCustomLink = (type: LinkType) => {
      return countFailingLocations((setting) => {
        return !!setting.customLinks?.find((link) => link.type === type && hasValue(link.url));
      });
    };

    const reportIfAnyMissing = (count: number, error: (typeof CommonErrors)[keyof typeof CommonErrors]) => {
      if (count > 0) {
        reportError(error, count);
      }
    };

    switch (linkType) {
      case 'phone':
        reportIfAnyMissing(
          countFailingLocations((setting) => hasValue(setting.selectedPhoneNumberId)),
          CommonErrors.MultiMissingPhone
        );
        return;
      case 'address':
        reportIfAnyMissing(
          countFailingLocations((setting) => hasValue(setting.locationAddress?.address)),
          CommonErrors.MultiMissingAddress
        );
        return;
      case 'email':
        reportIfAnyMissing(
          countFailingLocations((setting) => hasValue(setting.replyToEmail) || hasValue(setting.fromEmail)),
          CommonErrors.MultiMissingEmail
        );
        return;
      case 'review-google':
        reportIfAnyMissing(
          countFailingLocations(
            (setting) => setting.socialMediaMappings?.googleMapping?.setupState === SetupState.INTEGRATION_SUCCESS
          ),
          CommonErrors.MultiMissingGoogle
        );
        return;
      case 'review-facebook':
        reportIfAnyMissing(
          countFailingLocations(
            (setting) => setting.socialMediaMappings?.facebookMapping?.setupState === SetupState.INTEGRATION_SUCCESS
          ),
          CommonErrors.MultiMissingFacebook
        );
        return;
      case 'custom-web-page':
        reportIfAnyMissing(countMissingCustomLink(CustomLink_LinkType.WEB_PAGE), CommonErrors.MultiMissingWebPage);
        return;
      case 'custom-contact-page':
        reportIfAnyMissing(
          countMissingCustomLink(CustomLink_LinkType.CONTACT_PAGE),
          CommonErrors.MultiMissingContactPage
        );
        return;
      case 'custom-about-page':
        reportIfAnyMissing(countMissingCustomLink(CustomLink_LinkType.ABOUT_PAGE), CommonErrors.MultiMissingAboutPage);
        return;
      case 'custom-shop':
        reportIfAnyMissing(countMissingCustomLink(CustomLink_LinkType.SHOP), CommonErrors.MultiMissingShop);
        return;
      case 'custom-promotional-page':
        reportIfAnyMissing(
          countMissingCustomLink(CustomLink_LinkType.PROMOTIONAL_PAGE),
          CommonErrors.MultiMissingPromotionalPage
        );
        return;
      case 'custom-faq-page':
        reportIfAnyMissing(countMissingCustomLink(CustomLink_LinkType.FAQ_PAGE), CommonErrors.MultiMissingFaqPage);
        return;
      case 'custom-blog':
        reportIfAnyMissing(countMissingCustomLink(CustomLink_LinkType.BLOG), CommonErrors.MultiMissingBlog);
        return;
      case 'custom-help-center':
        reportIfAnyMissing(
          countMissingCustomLink(CustomLink_LinkType.HELP_CENTER),
          CommonErrors.MultiMissingHelpCenter
        );
        return;
      case 'custom-map':
        reportIfAnyMissing(countMissingCustomLink(CustomLink_LinkType.MAP), CommonErrors.MultiMissingMap);
        return;
      case 'custom-referral-page':
        reportIfAnyMissing(
          countMissingCustomLink(CustomLink_LinkType.REFERRAL_PAGE),
          CommonErrors.MultiMissingReferralPage
        );
        return;
      case 'custom-other':
        reportIfAnyMissing(countMissingCustomLink(CustomLink_LinkType.OTHER), CommonErrors.MultiMissingOther);
        return;
      default:
        return;
    }
  },
});
